import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ENGLISH_ABBREV } from "@bto/shared";
import { SendViewSignUpPageAction } from "./Services/CreateAccountService";
import { LOGIN_PAGE_URL } from "../../utils/urlConstants";
import { persistLanguagePreference } from "../../i18n/languagePreference";

/**
 * Resolves the lims-targeted Spanish flag for the anonymous signup page. The
 * client can't evaluate it pre-auth, so the page-load endpoint returns it
 * server-side (null while loading). AppContainer hands off the signup route, so
 * this also forces English once the flag resolves disabled.
 */
export const useSpanishSignupEnabled = (smsCode: string): boolean | null => {
  const history = useHistory();
  const { i18n } = useTranslation();
  const [spanishEnabled, setSpanishEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    let isCancelled = false;
    const resolveSpanishEnabled = async () => {
      setSpanishEnabled(null);
      try {
        const response = await SendViewSignUpPageAction(smsCode);
        // Bail after each await so a superseded smsCode can't redirect or persist a stale flag.
        if (isCancelled) return;
        if (response.status === 308) {
          history.push(LOGIN_PAGE_URL);
          return;
        }
        const data = await response.json().catch(() => ({}));
        if (isCancelled) return;
        setSpanishEnabled(Boolean(data?.spanish_enabled));
      } catch {
        if (!isCancelled) setSpanishEnabled(false); // fail closed to English
      }
    };
    resolveSpanishEnabled();
    return () => {
      isCancelled = true;
    };
  }, [smsCode, history]);

  useEffect(() => {
    if (spanishEnabled !== false) {
      return;
    }
    // Server resolved this requisition as not Spanish-eligible. Force English in
    // memory AND clear any stored ?lang=es preference (changeLanguage doesn't
    // persist — detection caching is off), so a later verify-email refresh, which
    // the journey exempts from force-English, doesn't re-init Spanish from a stale
    // localStorage value.
    if (i18n.language.split("-")[0] !== ENGLISH_ABBREV) {
      i18n.changeLanguage(ENGLISH_ABBREV);
    }
    persistLanguagePreference(ENGLISH_ABBREV);
  }, [spanishEnabled, i18n]);

  return spanishEnabled;
};
